// The plan card both Billing tabs use (RFP Manager and Extractor Studio): the plan on a plum panel,
// payment details on a white one beside it, and the small pieces each side is built from.
import React from "react";
import { CreditCard, Loader2 } from "lucide-react";

const CHIPS = {
  ok: { bg: "rgba(111,207,142,.14)", border: "rgba(111,207,142,.35)", text: "#9BE3B2", dot: "#6FCF8E" },
  attention: { bg: "rgba(231,182,225,.14)", border: "rgba(231,182,225,.4)", text: "#F1CBEC", dot: "#C95EBE" },
  danger: { bg: "rgba(240,128,104,.16)", border: "rgba(240,128,104,.4)", text: "#FFB9A8", dot: "#F08068" },
  muted: { bg: "rgba(255,255,255,.12)", border: "rgba(255,255,255,.2)", text: "rgba(255,255,255,.85)", dot: "rgba(255,255,255,.6)" },
};

// Buttons on the plum panel.
export const HERO_BTN = {
  primary:
    "inline-flex items-center gap-1.5 rounded-[9px] bg-white px-4 py-2 text-[14px] font-semibold text-[#2C1338] disabled:opacity-50",
  outline:
    "inline-flex items-center gap-1.5 rounded-[9px] border border-white/25 px-4 py-2 text-[14px] font-semibold text-white hover:bg-white/10 disabled:opacity-50",
  link: "text-[13px] font-medium text-white/72 underline disabled:opacity-50",
};

// `tone` is one of ok / attention / danger / muted; `label` is the chip's text.
export default function PlanHero({ tone, label, children, aside }) {
  const chip = CHIPS[tone] || CHIPS.muted;
  return (
    <div
      className="flex flex-wrap overflow-hidden rounded-[18px] border border-[#ECE9EE]"
      style={{ boxShadow: "0 6px 18px rgba(0,0,0,.06)" }}
    >
      <div
        className="flex flex-col gap-[18px] p-6 text-white"
        style={{
          flex: "1.55 1 400px",
          background:
            "radial-gradient(110% 120% at 100% 0%, rgba(201,94,190,.42), transparent 55%)," +
            "radial-gradient(80% 90% at 0% 100%, rgba(243,144,79,.2), transparent 60%), #2C1338",
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-semibold uppercase tracking-[.06em] text-white/72">Current plan</span>
          <span
            className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium"
            style={{ background: chip.bg, border: `1px solid ${chip.border}`, color: chip.text }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: chip.dot }} />
            {label}
          </span>
        </div>
        {children}
      </div>

      <div className="flex flex-col gap-[14px] bg-white p-6" style={{ flex: "1 1 300px" }}>
        {aside}
      </div>
    </div>
  );
}

export const HeroFigure = ({ value, caption }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-[40px] font-semibold leading-none tracking-[-0.035em] [font-variant-numeric:tabular-nums]">
      {value}
    </span>
    <span className="whitespace-nowrap text-[14.5px] text-white/72">{caption}</span>
  </div>
);

export const HeroBar = ({ pct }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,.12)" }}>
    <div
      className="h-full rounded-full"
      style={{ width: `${Math.min(100, pct)}%`, background: "linear-gradient(90deg,#f3904f,#C95EBE)" }}
    />
  </div>
);

// [label, value] pairs under a hairline, as many to a row as fit.
export const HeroStats = ({ stats, minWidth = 110 }) => (
  <div
    className="grid gap-y-2 border-t pt-3 text-[14px]"
    style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`, borderColor: "rgba(255,255,255,.12)" }}
  >
    {stats.map(([statLabel, value]) => (
      <div key={statLabel}>
        <div className="text-[11px] uppercase text-white/60">{statLabel}</div>
        <div className="font-semibold">{value}</div>
      </div>
    ))}
  </div>
);

export const NextCharge = ({ label, amount, note }) => (
  <div>
    <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-[#8A8A90]">{label}</span>
    <div className="mt-1 flex items-baseline gap-2">
      <span className="text-[24px] font-semibold text-[#1A1A1A] [font-variant-numeric:tabular-nums]">{amount}</span>
      <span className="text-[13px] text-[#52525B]">{note}</span>
    </div>
  </div>
);

// The card Stripe charges, or `empty` when there isn't one to show. No `onUpdate`, no Update button.
export const PaymentMethodRow = ({
  paymentMethod,
  empty = <p className="text-[13.5px] text-[#8A8A90]">No card on file</p>,
  onUpdate,
  disabled,
  busy,
}) => (
  <div className="flex items-center justify-between rounded-xl border border-[#ECE9EE] p-3">
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-[8px] bg-[#FAF9FB] text-[#52525B]">
        <CreditCard size={16} />
      </div>
      {paymentMethod ? (
        <div>
          <p className="text-[13.5px] font-medium text-[#1A1A1A]">
            {(paymentMethod.brand || "card").toUpperCase()} •••• {paymentMethod.last4}
          </p>
          <p className="text-[12px] text-[#8A8A90]">
            Expires {String(paymentMethod.exp_month).padStart(2, "0")}/{String(paymentMethod.exp_year).slice(-2)}
          </p>
        </div>
      ) : (
        empty
      )}
    </div>
    {onUpdate && (
      <button
        type="button"
        onClick={onUpdate}
        disabled={disabled}
        className="rounded-[8px] border border-[#ECE9EE] px-3 py-1.5 text-[13px] font-medium text-[#1A1A1A] disabled:opacity-50"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : "Update"}
      </button>
    )}
  </div>
);
